import React from 'react';
import { ArrowLeft, Trash2, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-container">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
        
        <div className="privacy-policy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>
          
          <section>
            <h2>1. Introduction</h2>
            <p>
              JustLabs ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us, including:</p>
            <ul>
              <li>Name and contact information (email address, phone number)</li>
              <li>Company information</li>
              <li>Project requirements and specifications</li>
              <li>Communication preferences</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you visit our website, we may automatically collect certain information, including:</p>
            <ul>
              <li>IP address and location data</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for various purposes, including:</p>
            <ul>
              <li>Providing and maintaining our services</li>
              <li>Communicating with you about projects and services</li>
              <li>Improving our website and services</li>
              <li>Analyzing usage patterns and trends</li>
              <li>Complying with legal obligations</li>
              <li>Marketing and promotional purposes (with your consent)</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:</p>
            <ul>
              <li>With your explicit consent</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and property</li>
              <li>In connection with a business transfer or merger</li>
              <li>With trusted service providers who assist us in operating our website and services</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
            </p>
          </section>

          <section>
            <h2>6. Cookies and Tracking Technologies</h2>
            <p>
              We may use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul>
              <li>Right to access and receive a copy of your personal information</li>
              <li>Right to rectify inaccurate personal information</li>
              <li>Right to erasure of your personal information</li>
              <li>Right to restrict processing of your personal information</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
          </section>

          <section id="data-deletion">
            <h2>8. User Data Deletion Policy & Requests</h2>
            <p>
              We believe in giving you full ownership and control over your personal data. In compliance with data protection laws and Google Play Developer Policies, you may at any time request the permanent deletion of your data and account records associated with JustLabs or any of our applications (including JustSolar, JustPDF, JustPrint, JustSaver, and JustConverter).
            </p>

            <div className="data-deletion-card">
              <div className="data-deletion-header">
                <Trash2 size={24} color="#dc2626" />
                <h3>How to Request Account & Data Deletion</h3>
              </div>
              <p>
                If you wish to have your data permanently deleted from our systems, please send an email to our support team at:
              </p>
              <p style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>
              </p>
              <p>
                To help us locate and process your request quickly, please provide the following details in your email:
              </p>
              <ul>
                <li><strong>Subject Line:</strong> Data Deletion Request - [Your Name / App Name]</li>
                <li><strong>Associated Email Address:</strong> The email address used during purchase or communication</li>
                <li><strong>Application Name(s):</strong> JustSolar, JustPDF, JustPrint, JustSaver, or JustConverter</li>
                <li><strong>Identifiers (Optional):</strong> Payment Transaction ID or Device ID (if requesting license revocation/deletion)</li>
              </ul>
              <p>
                <strong>Deletion Timeline & Process:</strong> Once we receive your email request, our support team will verify your request and permanently delete your user records, purchase histories, and support communications from our active databases within <strong>48 to 72 business hours</strong>. You will receive an email confirmation once the deletion is finalized.
              </p>

              <a 
                href="mailto:pankajacharyaorg@gmail.com?subject=Data%20Deletion%20Request&body=Hello%20JustLabs%20Support%2C%0A%0AI%20would%20like%20to%20request%20the%20permanent%20deletion%20of%20all%20my%20data%20associated%20with%20your%20services%20and%20applications.%0A%0APlease%20find%20my%20details%20below%3A%0A-%20Full%20Name%3A%20%0A-%20Registered%20Email%20Address%3A%20%0A-%20Application%20Name(s)%3A%20%0A-%20Device%20Identifier%20%2F%20Transaction%20ID%20(if%20any)%3A%20%0A-%20Additional%20Notes%3A%20%0A%0AThank%20you."
                className="data-deletion-btn"
              >
                <Send size={16} />
                Send Data Deletion Email
              </a>

              <div className="data-deletion-email-direct">
                Direct Contact: <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>
              </div>
            </div>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="contact-info">
              <p><strong>JustLabs</strong></p>
              <p>Email: <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a></p>
              <p>Sole Proprietor: Pankaj Acharya</p>
              <p>Website: <a href="https://justlabs.in">https://justlabs.in</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 